/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../theme/theme';

export enum Indicators {
  NEW = 'NEW',
  BETA = 'BETA',
  LEGACY = 'LEGACY'
}

export interface IndicatorProps {
  indicator?: Indicators;
}

export const useStyles = makeStyles(() => ({
  indicator : {
    color         : dlsColor('white'),
    verticalAlign : 'middle',
    marginRight   : '10px',
    padding       : '2px 6px',
    borderRadius  : '3px',
    fontFamily    : 'Roboto',
    textTransform : 'uppercase',
    fontSize      : '12px',
    fontWeight    : 500
  }
}));

export interface ComponentProps {
  variant: Indicators;
}

/**
 * The Indicator component is responsible for displaying a NEW, BETA, or LEGACY (or future ones) badge.
 *
 * ```typescript
 *
 *   import Indicator from '@ion/dls/src/components/indicators/Indicator';
 *
 *   <Indicator variant={Indicators.NEW} />
 *   <Indicator variant={Indicators.BETA} />
 *   <Indicator variant={Indicators.LEGACY} />
 *
 * ```
 */
export const Indicator = ({
  variant
}: ComponentProps) => {
  const classes    = useStyles();
  const indicators = translate('common:indicators', {returnObjects: true});
  return (
    <span className={classes.indicator}
      style={{backgroundColor: dlsColor(getIndicatorBackgroundColor(variant))}}>
      {indicators[variant.toLowerCase()]}
    </span>
  );
};

const getIndicatorBackgroundColor = (variant: Indicators): string => (
  variant === Indicators.LEGACY ? 'caBlue' : 'electricPurple'
);

export default React.memo(Indicator);
